// src/components/dashboard/DossierDetails.js

import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import DualTimeline from "./DualTimeline";
import { useAuth } from "../../context/AuthContext";
import { FileText, Clock, Info, ArrowLeft } from "lucide-react";
import DocumentsManager from "./shared/DocumentsManager";

const DossierDetails = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('timeline');

  // NOUVEAU: Détecter si on vient des notifications
  const searchParams = new URLSearchParams(location.search);
  const retour = searchParams.get('retour');
  const venantDesNotifications = retour === 'notifications';

  // DEBUG temporaire - à supprimer plus tard
  console.log('🔍 DEBUG retour:', retour, 'venantDesNotifications:', venantDesNotifications, 'URL:', location.pathname + location.search);

  useEffect(() => {
    const fetchDossier = async () => {
      try {
        const refDossier = doc(db, "dossiers", id);
        const snap = await getDoc(refDossier);
        if (snap.exists()) {
          setDossier(snap.data());
        } else {
          console.error("Dossier introuvable");
        }
      } catch (err) {
        console.error("Erreur chargement dossier:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchDossier();
  }, [id]);

  if (loading) return <div className="p-8">Chargement...</div>;
  if (!dossier) return <div className="p-8 text-red-600">Dossier non trouvé</div>;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-6 text-center">
        <h2 className="text-3xl font-bold">Détails du Dossier</h2>
        <p className="text-xl font-semibold">{dossier.clientName}</p>
        <button
          onClick={() => setShowModal(true)}
          className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 inline-flex items-center"
        >
          <Info className="w-4 h-4 mr-2" />
          Voir le détail du dossier
        </button>
      </div>

      {/* Navigation par onglets AVEC bouton retour */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex items-center justify-between">
          {/* NOUVEAU: Bouton retour aux notifications - À GAUCHE */}
          <div className="flex-1">
            {venantDesNotifications && (
              <Link
                to="/admin/notifications"
                className="inline-flex items-center py-2 px-4 border-2 border-orange-500 text-orange-600 hover:text-orange-700 hover:border-orange-600 font-medium text-sm rounded-lg transition-colors bg-orange-50 hover:bg-orange-100"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour aux notifications
              </Link>
            )}
          </div>
          
          {/* Onglets - AU CENTRE */}
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('timeline')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'timeline'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } inline-flex items-center`}
            >
              <Clock className="w-4 h-4 mr-2" />
              Timeline du projet
            </button>
            
            <button
              onClick={() => setActiveTab('documents')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'documents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } inline-flex items-center`}
            >
              <FileText className="w-4 h-4 mr-2" />
              Documents partagés
            </button>
          </div>
          
          {/* Espace vide - À DROITE pour équilibrer */}
          <div className="flex-1"></div>
        </nav>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'timeline' ? (
        <DualTimeline
          dossierId={id}
          chargeAffairePhases={dossier.phasesChargeAffaire || []}
          chefProjetPhases={dossier.phasesChefProjet || []}
        />
      ) : (
        <div className="bg-white rounded-lg border p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Centre de documents partagé</h3>
            <p className="text-sm text-gray-600">
              Espace de documents partagé entre l'équipe EnergeSync et le client {dossier.clientName}
            </p>
          </div>
          <DocumentsManager 
            dossierId={id}
            isAdmin={true}
            canEdit={true}
            showInternalDocs={true}
          />
        </div>
      )}

      <div className="mt-6 text-center">
        <Link to="/admin/dossiers" className="text-blue-600 hover:underline text-lg">
          Retour à la liste des dossiers
        </Link>
      </div>

      {/* Modal existant - inchangé */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-2xl w-full relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-2 right-4 text-gray-500 text-2xl hover:text-black"
            >
              &times;
            </button>
            <h3 className="text-2xl font-bold mb-4">Informations du dossier</h3>
            <div className="space-y-2">
              <p><strong>Titre :</strong> {dossier.title}</p>
              <p><strong>Description :</strong> {dossier.description}</p>
              <p><strong>Nom du client :</strong> {dossier.clientName}</p>
              <p><strong>Email :</strong> {dossier.clientEmail}</p>
              <p><strong>Téléphone :</strong> {dossier.clientPhone || "Non renseigné"}</p>
              <p><strong>Adresse :</strong> {dossier.clientAddress || "Non renseignée"}</p>
              <p><strong>GPS :</strong> {dossier.gps || "Non renseigné"}</p>
              <p><strong>Type de client :</strong> {dossier.typeClient}</p>
              <p><strong>Source de contact :</strong> {dossier.sourceContact}</p>
              <p><strong>Raccordement :</strong> {dossier.raccordement}</p>
              <p><strong>Date de création :</strong> {dossier.createdAt?.toDate?.().toLocaleDateString() || "-"}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DossierDetails;